import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js'

/**
 * Validate phone number format (FR-012, FR-019)
 * Accepts international E.164 format (e.g., +14155552671)
 */
export function validatePhoneNumber(phoneNumber: string): {
  isValid: boolean
  formatted?: string
  error?: string
} {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return {
      isValid: false,
      error: 'Phone number is required',
    }
  }

  try {
    // Check if valid phone number
    if (!isValidPhoneNumber(phoneNumber)) {
      return {
        isValid: false,
        error: 'Please enter a valid phone number in international format (e.g., +14155552671)',
      }
    }

    // Parse and format to E.164
    const parsed = parsePhoneNumber(phoneNumber)
    return {
      isValid: true,
      formatted: parsed.format('E.164'),
    }
  } catch {
    return {
      isValid: false,
      error: 'Please enter a valid phone number in international format (e.g., +14155552671)',
    }
  }
}

/**
 * Validate email address format (FR-014, FR-020)
 * Uses standard email regex pattern
 */
export function validateEmail(email: string): {
  isValid: boolean
  error?: string
} {
  if (!email || email.trim() === '') {
    return {
      isValid: false,
      error: 'Email address is required',
    }
  }

  // Standard email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address',
    }
  }

  return {
    isValid: true,
  }
}

/**
 * Validate optional email (returns valid for empty string)
 */
export function validateOptionalEmail(email: string): {
  isValid: boolean
  error?: string
} {
  if (!email || email.trim() === '') {
    return { isValid: true }
  }

  return validateEmail(email)
}

/**
 * Validate timezone format (FR-027)
 * Checks against IANA timezone identifiers
 */
export function validateTimezone(timezone: string): {
  isValid: boolean
  error?: string
} {
  if (!timezone || timezone.trim() === '') {
    return {
      isValid: false,
      error: 'Timezone is required',
    }
  }

  try {
    // Check if timezone is valid by trying to use it
    Intl.DateTimeFormat(undefined, { timeZone: timezone })
    return {
      isValid: true,
    }
  } catch {
    return {
      isValid: false,
      error: 'Invalid timezone',
    }
  }
}

/**
 * Validate language preference (FR-015, FR-042)
 */
export function validateLanguage(language: string): {
  isValid: boolean
  error?: string
} {
  const validLanguages = ['en', 'zh-Hant']

  if (!validLanguages.includes(language)) {
    return {
      isValid: false,
      error: "Invalid language. Must be 'en' or 'zh-Hant'",
    }
  }

  return {
    isValid: true,
  }
}

/**
 * Validate name field (Account Settings FR-010, FR-019)
 * Must be 2-100 characters
 */
export function validateName(name: string): {
  isValid: boolean
  error?: string
} {
  if (!name || name.trim() === '') {
    return {
      isValid: false,
      error: 'Name is required',
    }
  }

  const trimmed = name.trim()

  if (trimmed.length < 2) {
    return {
      isValid: false,
      error: 'Name must be at least 2 characters',
    }
  }

  if (trimmed.length > 100) {
    return {
      isValid: false,
      error: 'Name must be less than 100 characters',
    }
  }

  return {
    isValid: true,
  }
}

/**
 * Validate optional name field (allows empty)
 */
export function validateOptionalName(name: string): {
  isValid: boolean
  error?: string
} {
  if (!name || name.trim() === '') {
    return { isValid: true }
  }

  return validateName(name)
}
